import { Add, Delete, ViewAgenda } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { DragEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { BuilderFieldDef, BuilderStep, PrmFieldType } from "../../types/formBuilder.types";
import CanvasField from "./CanvasField";
import { PRM_FIELD_TYPE_MIME } from "./FieldPalette";

interface FormCanvasProps {
    fields: BuilderFieldDef[];
    steps: BuilderStep[];
    selectedField: string | null;
    activeStepIndex: number | null;
    onAddField: (fieldType: PrmFieldType, index?: number, stepIndex?: number) => void;
    onSelectField: (name: string | null) => void;
    onSelectStep: (stepIndex: number | null) => void;
    onRemoveField: (name: string) => void;
    onMoveField: (fromIndex: number, toIndex: number, stepIndex?: number | null) => void;
    onRemoveStep: (index: number) => void;
}

const readPaletteType = (event: DragEvent<HTMLElement>): PrmFieldType | null => {
    const value = event.dataTransfer.getData(PRM_FIELD_TYPE_MIME);
    return value ? value as PrmFieldType : null;
};

const FormCanvas = ({ fields, steps, selectedField, activeStepIndex, onAddField, onSelectField, onSelectStep, onRemoveField, onMoveField, onRemoveStep }: FormCanvasProps) => {
    const { t } = useTranslation();
    const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

    const handleDragOver = (event: DragEvent<HTMLElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = readPaletteType(event) ? "copy" : "move";
    };

    const handleDrop = (event: DragEvent<HTMLElement>, targetIndex: number, stepIndex?: number | null) => {
        event.preventDefault();
        const paletteType = readPaletteType(event);

        if (paletteType) {
            onAddField(paletteType, targetIndex, stepIndex === null ? undefined : stepIndex);
            setDraggingIndex(null);
            return;
        }

        if (draggingIndex === null || draggingIndex === targetIndex) {
            setDraggingIndex(null);
            return;
        }

        onMoveField(draggingIndex, targetIndex, stepIndex);
        setDraggingIndex(null);
    };

    const getGroupEndIndex = (groupFields: BuilderFieldDef[]) => {
        if (!groupFields.length) return fields.length;
        const indexes = groupFields.map((field) => fields.findIndex((candidate) => candidate.name === field.name)).filter((index) => index >= 0);
        return indexes.length ? Math.max(...indexes) + 1 : fields.length;
    };

    const renderDropZone = (label: string, targetIndex: number, stepIndex?: number | null) => (
        <Box
            className="service-builder__drop-zone"
            onDragOver={handleDragOver}
            onDrop={(event) => handleDrop(event, targetIndex, stepIndex)}
        >
            {t(label)}
        </Box>
    );

    const resolveStepTitle = (step: BuilderStep, index: number) => {
        const fallback = step.title || `Step ${index + 1}`;
        if (/^Step \d+$/.test(fallback)) return t("Step {{number}}", { number: index + 1 });
        return step.title ? t(step.title, { defaultValue: fallback }) : fallback;
    };

    const renderFields = (groupFields: BuilderFieldDef[], stepIndex?: number | null) => (
        <Stack spacing={1}>
            {groupFields.map((field) => {
                const globalIndex = fields.findIndex((candidate) => candidate.name === field.name);
                return (
                    <CanvasField
                        key={field.name}
                        field={field}
                        selected={selectedField === field.name}
                        index={globalIndex}
                        total={fields.length}
                        dragging={draggingIndex === globalIndex}
                        onSelect={onSelectField}
                        onRemove={onRemoveField}
                        onMove={onMoveField}
                        onDragStart={setDraggingIndex}
                        onDragOver={handleDragOver as any}
                        onDrop={(event, targetIndex) => handleDrop(event, targetIndex, stepIndex)}
                        onDragEnd={() => setDraggingIndex(null)}
                    />
                );
            })}
            {renderDropZone("Drop field here", getGroupEndIndex(groupFields), stepIndex)}
        </Stack>
    );

    return (
        <Box className="service-builder__canvas" onClick={() => onSelectField(null)}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" className="service-builder__panel-header">
                <Stack direction="row" spacing={1} alignItems="center">
                    <ViewAgenda fontSize="small" />
                    <Typography variant="subtitle2">{t("service_builder.canvas", { defaultValue: "Canvas" })}</Typography>
                </Stack>
                <Button size="small" startIcon={<Add />} onClick={(event) => { event.stopPropagation(); onAddField("text", undefined, activeStepIndex ?? undefined); }}>
                    {t("Text field")}
                </Button>
            </Stack>
            <Divider />
            <Box
                className="service-builder__canvas-scroll"
                onClick={(event) => event.stopPropagation()}
                onDragOver={handleDragOver}
                onDrop={(event) => handleDrop(event, fields.length, steps.length ? activeStepIndex : null)}
            >
                {fields.length === 0 && steps.length === 0 ? (
                    <Box className="service-builder__empty-canvas">
                        <Typography variant="subtitle2">{t("Start with a field from the palette.")}</Typography>
                        <Typography variant="body2">{t("Click a field or drag it into this canvas.")}</Typography>
                        {renderDropZone("Drop first field here", 0, null)}
                    </Box>
                ) : steps.length > 0 ? (
                    <Stack spacing={2}>
                        {steps.map((step, index) => {
                            const stepFields = fields.filter((field) => field.stepIndex === index || step.fields.includes(field.name));
                            const active = activeStepIndex === index;
                            return (
                                <Box
                                    className={`service-builder__step ${active ? "service-builder__step--active" : ""}`}
                                    key={`${step.title}-${index}`}
                                    onClick={() => onSelectStep(index)}
                                    onDragOver={handleDragOver}
                                    onDrop={(event) => handleDrop(event, getGroupEndIndex(stepFields), index)}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography className="service-builder__step-title">{resolveStepTitle(step, index)}</Typography>
                                        <Tooltip title={t("Delete step")}>
                                            <IconButton size="small" color="error" onClick={(event) => { event.stopPropagation(); onRemoveStep(index); }}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                    {stepFields.length ? renderFields(stepFields, index) : renderDropZone("Drop fields here", fields.length, index)}
                                </Box>
                            );
                        })}
                    </Stack>
                ) : renderFields(fields, null)}
            </Box>
        </Box>
    );
};

export default FormCanvas;
